// client/src/components/admin/MobileEventList.js
import React from 'react'
import EventActionsDropdown from './EventActionsDropdown'

/**
 * Mobile-optimized event list component.
 * Displays telemetry events in a card-based layout for mobile devices.
 *
 * @param {Array} events - Array of telemetry events to display
 * @param {Boolean} eventsLoading - Loading state for events
 * @param {Function} onViewDetails - Handler for viewing event details
 * @param {Function} onCopyId - Handler for copying event ID
 * @param {Function} onCopyPayload - Handler for copying event payload JSON
 * @param {Function} onJumpToUser - Handler for navigating to user profile
 * @param {Function} getEventTypeIcon - Function to get emoji icon for event type
 * @param {Function} getTagCategory - Function to categorize event tags
 * @param {Function} getTagCategoryColor - Function to get Bootstrap color for tag category
 * @param {Function} formatDateTime - Function to format timestamp
 */
function MobileEventList({
  events,
  eventsLoading,
  onViewDetails,
  onCopyId,
  onCopyPayload,
  onJumpToUser,
  getEventTypeIcon,
  getTagCategory,
  getTagCategoryColor,
  formatDateTime,
}) {
  if (eventsLoading) {
    return (
      <div className="d-md-none text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading events...</span>
        </div>
      </div>
    )
  }

  if (events.length === 0) {
    return (
      <div className="d-md-none text-center text-muted py-4">
        <p>No recent events found</p>
      </div>
    )
  }

  return (
    <div className="d-md-none">
      {events.map((event, index) => {
        const category = getTagCategory(event.tag)
        return (
          <div
            key={event.id ?? `${event.event_type}-${event.timestamp}-${index}`}
            className="card mb-3 event-card"
          >
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start event-card-row">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center mb-2">
                    <span className="me-2" title={event.event_type}>
                      {getEventTypeIcon(event.event_type)}
                    </span>
                    <span
                      className={`badge bg-${getTagCategoryColor(
                        category,
                      )} me-2`}
                      style={{ fontSize: '0.7rem' }}
                    >
                      {category}
                    </span>
                    {event.user_id ? (
                      <span
                        className="badge bg-success user-badge-popover"
                        data-user-id={event.user_id}
                        tabIndex={0}
                        role="button"
                        aria-label={`View details for user ${event.user_id}`}
                      >
                        {event.user_id}
                      </span>
                    ) : (
                      <span className="badge bg-secondary">Anonymous</span>
                    )}
                  </div>
                  <h6 className="card-title mb-1">
                    <code style={{ fontSize: '0.85rem' }}>{event.tag}</code>
                  </h6>
                  <small className="text-muted">
                    {event.page && `${event.page} • `}
                    {formatDateTime(event.timestamp)}
                    {event.metadata &&
                      Object.keys(event.metadata).length > 0 &&
                      ' • Has metadata'}
                  </small>
                </div>
                <div className="event-card-actions">
                  <EventActionsDropdown
                    event={event}
                    onViewDetails={onViewDetails}
                    onCopyId={onCopyId}
                    onCopyPayload={onCopyPayload}
                    onJumpToUser={onJumpToUser}
                  />
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default MobileEventList
